import { createPortal } from 'react-dom'
import type { Dispatch, SetStateAction } from 'react'
import { TEMPLATE_CATEGORIES, TEMPLATE_INDUSTRIES, TEMPLATE_LANGUAGES, type TemplateFacets, type TemplateLanguage } from '@webbuilder/schemas'
import type { AdminTemplate } from '../lib/api'
import { TemplateFacetEditor } from './TemplateFacetEditor'
import { TemplateAiReadiness } from './TemplateAiReadiness'
import { IconChevronDown, IconLoader2, IconPhotoPlus } from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'

export type TemplateForm = {
  name: string
  slug: string
  kind: 'website' | 'page' | 'section' | 'header' | 'footer' | 'component'
  category: string
  description: string
  /** Structured taxonomy facets — the single tagging surface (styleTags/aiTags are derived on save). */
  facets: TemplateFacets
  isPremium: boolean
  isPublic: boolean
  thumbnailUrl: string
}

type Props = {
  token: string
  open: boolean
  editing: AdminTemplate | null
  form: TemplateForm
  setForm: Dispatch<SetStateAction<TemplateForm>>
  saving: boolean
  thumbnailUploading: boolean
  error: string
  onClose: () => void
  onThumbnailFile: (file: File) => void
  onSave: () => void
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

/**
 * La ficha de un template: lo que alimenta la biblioteca y la IA. El diseño
 * vive en el canvas.
 *
 * Por partes, de lo imprescindible a lo opcional: lo básico, lo que la IA usa
 * para elegirlo y, plegado, lo demás. `media` y `columns` ya no se piden: se
 * deducen del contenido (ver `deriveTemplateFacets`) y se enseñan en la
 * tarjeta de «Lista para la IA».
 */
export function TemplateDataModal({
  token,
  open,
  editing,
  form,
  setForm,
  saving,
  thumbnailUploading,
  error,
  onClose,
  onThumbnailFile,
  onSave,
}: Props) {
  const { t } = useI18n()
  if (!open) return null
  const isSite = form.kind === 'website'
  const setFacets = (facets: TemplateFacets) => setForm((previous) => ({ ...previous, facets }))

  return createPortal(
    <div
      className="dash-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose()
      }}
    >
      <section className="dash-modal dash-template-modal" role="dialog" aria-modal="true" aria-label={t(editing ? 'templateModal.editTemplate' : 'templateModal.newTemplate')}>
        <header>
          <h2>{t(isSite ? (editing ? 'templateModal.editSite' : 'templateModal.newSite') : (editing ? 'templateModal.editTemplate' : 'templateModal.newTemplate'))}</h2>
          <p>{t(isSite ? 'templateModal.siteLead' : 'templateModal.blockLead')}</p>
        </header>

        {editing && !isSite ? (
          <TemplateAiReadiness
            token={token}
            templateId={editing.id}
            category={form.category}
            description={form.description}
            facets={form.facets}
          />
        ) : null}

        <div className="dash-template-form-group">
          <h3>{t('templateModal.sectionBasics')}</h3>
          <div className="dash-modal-grid">
            <label className="dash-field">
              <span>{t('templateModal.name')}</span>
              <input
                value={form.name}
                onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
                onBlur={() => setForm((previous) => ({ ...previous, slug: previous.slug.trim() || toSlug(previous.name) }))}
                autoFocus
                placeholder={t('templateModal.namePlaceholder')}
              />
            </label>
            {isSite ? (
              <label className="dash-field">
                <span>{t('templateModal.kind')}</span>
                <input value={t('templateModal.fullPage')} readOnly disabled />
              </label>
            ) : (
              <label className="dash-field">
                <span>{t('templateModal.kind')}</span>
                <select value={form.kind} onChange={(event) => setForm((previous) => ({ ...previous, kind: event.target.value as TemplateForm['kind'] }))}>
                  <option value="page">{t('templateModal.kindPage')}</option>
                  <option value="section">{t('templateModal.kindSection')}</option>
                  <option value="header">{t('templateModal.kindHeader')}</option>
                  <option value="footer">{t('templateModal.kindFooter')}</option>
                  <option value="component">{t('templateModal.kindComponent')}</option>
                </select>
              </label>
            )}
            {/* Un bloque se clasifica por lo que ES (hero, precios…); un sitio
                entero, por el negocio al que va. Es el mismo campo —la columna
                `category`—, con la lista que toca a cada uno. */}
            <label className="dash-field">
              <span>{t(isSite ? 'templateModal.industry' : 'templateModal.category')}</span>
              <select
                value={(isSite ? TEMPLATE_INDUSTRIES : TEMPLATE_CATEGORIES).some((entry) => entry.value === form.category) ? form.category : ''}
                onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value }))}
              >
                <option value="" disabled>{t(isSite ? 'templateModal.pickIndustry' : 'templateModal.pickCategory')}</option>
                {(isSite ? TEMPLATE_INDUSTRIES : TEMPLATE_CATEGORIES).map((option) => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </select>
            </label>
            {/* El idioma de sus textos: se ofrece sólo a sitios en ese idioma
                (`templateFitsLanguage`). Sin idioma, a todos. */}
            <label className="dash-field">
              <span>{t('templateModal.language')}</span>
              <select
                value={form.facets.language ?? ''}
                onChange={(event) => {
                  const value = event.target.value as TemplateLanguage | ''
                  const { language: _language, ...rest } = form.facets
                  setFacets(value ? { ...rest, language: value } : rest)
                }}
              >
                <option value="">{t('templateModal.languageAny')}</option>
                {TEMPLATE_LANGUAGES.map((language) => (
                  <option key={language} value={language}>{t(`templateModal.language.${language}`)}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="dash-field">
            <span>{t('templateModal.description')}</span>
            <textarea value={form.description} onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))} placeholder={t(isSite ? 'templateModal.descriptionPlaceholderSite' : 'templateModal.descriptionPlaceholder')} rows={2} />
            {isSite ? null : <small className="dash-field-hint">{t('templateModal.descriptionHint')}</small>}
          </label>
        </div>

        {isSite ? (
          <div className="dash-field">
            <span>{t('templateModal.thumbnail')}</span>
            <div className="dash-template-thumb-field">
              <div className="dash-template-thumb-preview" aria-hidden="true">
                {form.thumbnailUrl ? <img src={form.thumbnailUrl} alt="" /> : <IconPhotoPlus size={22} />}
              </div>
              <div className="dash-template-thumb-actions">
                <label className="dash-secondary-btn" style={{ cursor: 'pointer' }}>
                  {thumbnailUploading ? <IconLoader2 className="dash-spin" size={14} /> : <IconPhotoPlus size={14} />}
                  {t(thumbnailUploading ? 'templateModal.uploading' : (form.thumbnailUrl ? 'templateModal.changeImage' : 'templateModal.uploadImage'))}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) onThumbnailFile(file)
                      event.target.value = ''
                    }}
                  />
                </label>
                {form.thumbnailUrl ? <button type="button" className="dash-ghost-btn" onClick={() => setForm((previous) => ({ ...previous, thumbnailUrl: '' }))}>{t('templateModal.removeImage')}</button> : null}
                <p className="dash-field-hint">{t('templateModal.thumbnailHint')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="dash-template-form-group">
            <h3>{t('templateModal.sectionAi')}</h3>
            <p className="dash-field-hint">{t('templateModal.sectionAiHint')}</p>
            <TemplateFacetEditor keys={['layout', 'colorScheme']} facets={form.facets} showBlockType={form.kind === 'component'} onChange={setFacets} />
            {/* Si la IA lo usa al crear sitios. Apagado (`manualOnly`), se queda
                en la biblioteca para ponerlo a mano. Encendido por defecto. */}
            <div className="dash-template-manual">
              <span>
                <strong>{t('templateModal.aiUse')}</strong>
                <small>{t('templateModal.aiUseHint')}</small>
              </span>
              <button
                className={`dash-general-toggle${form.facets.manualOnly ? '' : ' is-on'}`}
                type="button"
                role="switch"
                aria-checked={!form.facets.manualOnly}
                aria-label={t('templateModal.aiUse')}
                onClick={() => {
                  const { manualOnly: _drop, ...rest } = form.facets
                  setFacets(form.facets.manualOnly ? rest : { ...rest, manualOnly: true })
                }}
              >
                <span />
              </button>
            </div>
            {/* Un bloque de varias sucursales: la IA sólo lo usa cuando el
                negocio tiene ese número (ver `fitsLocations` en el worker). */}
            <label className="dash-field">
              <span>{t('templateModal.locations')}</span>
              <select
                value={String(form.facets.locations ?? '')}
                onChange={(event) => {
                  const { locations: _drop, ...rest } = form.facets
                  const value = event.target.value
                  setFacets(value === '2' || value === '3' ? { ...rest, locations: Number(value) as 2 | 3 } : value === 'list' ? { ...rest, locations: 'list' } : rest)
                }}
              >
                <option value="">{t('templateModal.locations.one')}</option>
                <option value="2">{t('templateModal.locations.two')}</option>
                <option value="3">{t('templateModal.locations.three')}</option>
                <option value="list">{t('templateModal.locations.list')}</option>
              </select>
              <small className="dash-field-hint">{t('templateModal.locationsHint')}</small>
            </label>
          </div>
        )}

        <details className="dash-template-more">
          <summary>
            <span>
              <strong>{t('templateModal.more')}</strong>
              <small>{t('templateModal.moreHint')}</small>
            </span>
            <IconChevronDown size={16} aria-hidden="true" />
          </summary>
          <div className="dash-template-more-body">
            <label className="dash-field">
              <span>{t('templateModal.slug')}</span>
              <input value={form.slug} onChange={(event) => setForm((previous) => ({ ...previous, slug: toSlug(event.target.value) }))} placeholder={t('templateModal.slugPlaceholder')} />
            </label>
            <div className="dash-check-row">
              <label>
                <input type="checkbox" checked={form.isPremium} onChange={(event) => setForm((previous) => ({ ...previous, isPremium: event.target.checked }))} />
                {t('templateModal.premium')}
              </label>
            </div>
          </div>
        </details>

        {!editing ? (
          <div className="dash-check-row">
            <label>
              <input type="checkbox" checked={form.isPublic} onChange={(event) => setForm((previous) => ({ ...previous, isPublic: event.target.checked }))} />
              {t('templateModal.publishOnCreate')}
            </label>
          </div>
        ) : null}

        {error ? <p className="dash-ai-error">{error}</p> : null}
        <footer>
          <button className="dash-secondary-btn" type="button" onClick={onClose} disabled={saving}>{t('templateModal.cancel')}</button>
          <button className="dash-primary-btn" type="button" onClick={onSave} disabled={saving || !form.name.trim()}>
            {saving ? <IconLoader2 className="dash-spin" size={14} /> : null}
            {t(saving ? 'templateModal.saving' : 'templateModal.save')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
